import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Download, Calendar, Loader2, MapPin, Mail, Phone, User2 } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import { SECRETARY } from "@/lib/content";
import { useCompany } from "@/lib/content-context";
import { api, API_BASE } from "@/lib/api";
import { DOC_TAXONOMY } from "@/lib/documents";

const BACKEND_ROOT = API_BASE.replace(/\/api$/, "");
const CATS = DOC_TAXONOMY.shareholder.categories;

export default function ShareholderInformation() {
  usePageTitle("Shareholder Information");
  const company = useCompany();
  const [tab, setTab] = useState(CATS[0].slug);
  const [docsByCat, setDocsByCat] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/documents", { params: { section: "shareholder" } });
        if (cancelled) return;
        const grouped = {};
        for (const c of CATS) grouped[c.slug] = [];
        for (const d of data) (grouped[d.category] ||= []).push(d);
        setDocsByCat(grouped);
      } catch {
        if (!cancelled) setDocsByCat({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const current = docsByCat[tab] || [];

  return (
    <div data-testid="shareholder-information-page">
      {/* HERO */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-4">
            <span className="text-[#5C5A56]">Investor Relations</span>
            <span className="mx-3 text-[#5C5A56]/40">/</span>
            <span>Shareholder Information</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            Shareholder<br/><span className="italic">Information.</span>
          </h1>
          <p className="mt-8 text-[#5C5A56] text-lg leading-relaxed max-w-2xl">
            Statutory disclosures, policies and shareholder services for GESL Spinners Limited.
            Each section below holds the relevant published documents.
          </p>
        </div>
      </section>

      {/* TABS */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">Sections</div>
          <div data-testid="sh-tabs" className="flex flex-wrap gap-x-1 gap-y-2 border-b border-[#1A1A1A]/15 mb-8">
            {CATS.map((c) => (
              <button
                key={c.slug}
                data-testid={`sh-tab-${c.slug}`}
                onClick={() => setTab(c.slug)}
                className={`px-4 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors ${
                  tab === c.slug
                    ? "border-[#C25934] text-[#1A1A1A] font-medium"
                    : "border-transparent text-[#5C5A56] hover:text-[#1A1A1A]"
                }`}
              >
                {c.label}
                <span className="ml-2 font-mono-ed text-[10px] text-[#5C5A56]">
                  {docsByCat[c.slug]?.length ?? 0}
                </span>
              </button>
            ))}
          </div>

          <DocList loading={loading} docs={current} emptyLabel={CATS.find(c => c.slug === tab)?.label} />
        </div>
      </section>

      {/* COMPANY SECRETARY / CONTACT */}
      <section className="py-20 bg-[#EAE6DF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-5">Investor Service Contact</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              Reach the<br/>Company Secretary.
            </h2>
            <p className="mt-5 text-[#5C5A56] leading-relaxed">
              For investor grievances, share transfer queries, statutory record requests and
              other shareholder services.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#1A1A1A] text-[#F8F7F4] p-10 md:p-12">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#2E4A3D] flex items-center justify-center">
                  <User2 size={22} />
                </div>
                <div>
                  <div className="overline text-[#C25934]">Company Secretary</div>
                  <div className="font-serif-display text-2xl md:text-3xl">{SECRETARY.name}</div>
                </div>
              </div>

              <div className="divider-line bg-white/15 my-8" />

              <div className="space-y-5 text-sm">
                <div className="flex gap-3 items-start">
                  <MapPin size={18} className="text-[#C25934] shrink-0 mt-0.5" />
                  <span data-testid="sh-address" className="text-[#D9D4CC] leading-relaxed">
                    {company.addressLines.map((l, i) => <span key={i}>{l}<br/></span>)}
                  </span>
                </div>
                <div className="flex gap-3 items-center">
                  <Phone size={18} className="text-[#C25934]" />
                  <a data-testid="sh-phone" href={`tel:${company.phoneRaw}`} className="link-underline text-[#D9D4CC]">{company.phone}</a>
                </div>
                <div className="flex gap-3 items-center">
                  <Mail size={18} className="text-[#C25934]" />
                  <a data-testid="sh-email" href={`mailto:${company.email}`} className="link-underline text-[#D9D4CC]">{company.email}</a>
                </div>
              </div>

              <div className="mt-8 font-mono-ed text-[10px] tracking-widest text-[#D9D4CC]/70">
                CIN · {company.cin}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-serif-display text-3xl md:text-5xl max-w-2xl">
            Need financial statements?
          </h3>
          <Link to="/investor-relations/financial-statements" data-testid="sh-financial-cta" className="btn-primary">
            Financial Statements <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function DocList({ loading, docs, emptyLabel }) {
  if (loading) {
    return (
      <div data-testid="docs-loading" className="border border-[#1A1A1A]/10 bg-[#F8F7F4] p-8 text-sm text-[#5C5A56] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading documents…
      </div>
    );
  }
  if (!docs || docs.length === 0) {
    return (
      <div data-testid="docs-empty" className="border border-[#1A1A1A]/10 bg-[#F8F7F4] p-10 text-center text-[#5C5A56]">
        No {emptyLabel || "documents"} have been published yet.
      </div>
    );
  }
  return (
    <div className="border border-[#1A1A1A]/10 bg-[#F8F7F4]">
      <div className="hidden md:grid grid-cols-12 px-8 py-4 bg-[#EAE6DF] border-b border-[#1A1A1A]/10 overline">
        <div className="col-span-3">Period</div>
        <div className="col-span-7">Document</div>
        <div className="col-span-2 text-right">Action</div>
      </div>
      {docs.map((r) => (
        <div
          key={r.id}
          data-testid={`doc-row-${r.id}`}
          className="grid grid-cols-1 md:grid-cols-12 px-8 py-6 border-b border-[#1A1A1A]/10 last:border-b-0 items-center gap-3"
        >
          <div className="md:col-span-3 flex items-center gap-3">
            <Calendar size={16} className="text-[#C25934]" />
            <span className="font-mono-ed text-xs tracking-wider text-[#1A1A1A]">{r.period || "—"}</span>
          </div>
          <div className="md:col-span-7 flex items-center gap-3">
            <FileText size={18} className="text-[#2E4A3D] shrink-0" />
            <div>
              <div className="font-serif-display text-xl leading-tight">{r.title}</div>
              <div className="text-[10px] text-[#5C5A56] font-mono-ed mt-1">
                {(r.size_bytes / 1024).toFixed(0)} KB · {r.file_name}
              </div>
            </div>
          </div>
          <div className="md:col-span-2 md:text-right">
            <a
              href={`${BACKEND_ROOT}${r.download_url}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`download-doc-${r.id}`}
              className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 bg-[#2E4A3D] text-[#F8F7F4] uppercase tracking-widest hover:bg-[#1C2E26] transition-colors"
            >
              <Download size={12} /> Download
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
